// ADR-0149 §4 — pure serving-status logic for the Platform Knowledge meter.
// Kept out of the route component so it is unit-testable (console tests are
// pure-function tests; there is no render harness).

export type ServingRow = {
  surface_id: string;
  agent_type: string;
  budget_chars: number;
  selected_entries: number;
  selected_chars: number;
  served_entries: number;
  served_chars: number;
  dropped: { name: string; description: string; priority: number; chars: number }[];
  arbitrary_truncation: boolean;
  unreachable: { name: string; description: string; scope_tags: string[] }[];
  context_dependent: boolean;
  /** ADR-0150 §3.6: class-C entries indexed for this surface (never budgeted). */
  on_demand_entries: number;
  /** ADR-0150 §3.6: rendered index-block chars (forcibly resident). */
  index_chars: number;
  /** ADR-0150 §3.6: entries serving as class A by default (pre-classification). */
  unclassified_entries: number;
};

export type ServingStatus = { cls: string; label: string; rank: number };

/**
 * Status for one surface. Lower `rank` = worse = sorts first. Over-budget wins
 * over an unmanaged cut wins over base-load (per-request) wins over fits;
 * foundational-band overflow is its own worst case (mandatory guidance cut).
 */
export function servingStatus(r: ServingRow): ServingStatus {
  if (r.dropped.length > 0) {
    const foundational = r.dropped.some((d) => d.priority < 50);
    return foundational
      ? { cls: "pillBad", label: "Foundational band over budget", rank: 0 }
      : { cls: "pillBad", label: "Over budget", rank: 1 };
  }
  if (r.arbitrary_truncation) return { cls: "pillWarn", label: "Unmanaged cut", rank: 2 };
  if (r.context_dependent) return { cls: "pillNeutral", label: "Base load", rank: 3 };
  if (r.unreachable.length > 0) return { cls: "pillWarn", label: "Has unreachable", rank: 2 };
  return { cls: "pillOk", label: "Fits", rank: 4 };
}

export type PerRowStatus = { cls: string; label: string; title: string };

const humanize = (s: string) => s.replace(/_/g, " ");

/**
 * Per-entry serving status for one agent filter, keyed by entry name. Unreachable
 * (never selected) takes precedence over dropped (selected but over budget). An
 * entry that is served has no entry in the map — the common case stays quiet.
 * An agent served by more than one surface (reactive_viz) aggregates across them.
 */
export function perRowServingStatus(rows: ServingRow[], agentType: string): Map<string, PerRowStatus> {
  const map = new Map<string, PerRowStatus>();
  if (!agentType) return map;
  const mine = rows.filter((r) => r.agent_type === agentType);
  for (const r of mine) {
    for (const u of r.unreachable) {
      if (!map.has(u.name)) {
        map.set(u.name, {
          cls: "pillWarn",
          label: "Unreachable",
          title: `scope_tags [${u.scope_tags.join(", ")}] — not admitted by the ${humanize(r.surface_id)} selection, so this entry never loads for this agent.`,
        });
      }
    }
  }
  for (const r of mine) {
    const droppedChars = r.dropped.reduce((s, d) => s + d.chars, 0);
    for (const d of r.dropped) {
      if (!map.has(d.name)) {
        map.set(d.name, {
          cls: "pillBad",
          label: "Not served",
          title: `Dropped at the ${r.budget_chars.toLocaleString()}-character budget of ${humanize(r.surface_id)} — ${droppedChars.toLocaleString()} characters of lower-priority entries are not reaching the prompt.`,
        });
      }
    }
  }
  return map;
}
